import React from "react";
import {
    Box,
    Typography,
    Container,
    Paper,
    Grid,
    Divider,
} from "@mui/material";

import PaymentsIcon from "@mui/icons-material/Payments";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import GavelIcon from "@mui/icons-material/Gavel";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";

import BgImage from "../../assets/img/subheader-back.jpg";

const sections = [
    {
        icon: <PaymentsIcon sx={{ color: "#ffd786", fontSize: 30 }} />,
        title: "1. Non-Refundable Donations",
        content:
            "All donations and purchases made to Vex Conquer are final and non-refundable. Donations are used to support server hosting, maintenance, development, and future content updates.",
    },
    {
        icon: <WarningAmberIcon sx={{ color: "#ffd786", fontSize: 30 }} />,
        title: "2. Exceptional Cases",
        content:
            "Refund requests may only be considered in rare situations such as duplicate transactions or verified payment processing errors. Every request will be reviewed individually by the administration team.",
    },
    {
        icon: <GavelIcon sx={{ color: "#ffd786", fontSize: 30 }} />,
        title: "3. Chargebacks & Disputes",
        content:
            "Unauthorized chargebacks, false disputes, or payment reversals through payment providers may result in permanent suspension of all related accounts and services.",
    },
    {
        icon: <SupportAgentIcon sx={{ color: "#ffd786", fontSize: 30 }} />,
        title: "4. Support Assistance",
        content:
            "If you experience payment issues or transaction concerns, please contact the Vex Conquer staff through our official Discord server or website support channels before filing any disputes.",
    },
];

const RefundPolicy = () => {
    return (
        <Box
            sx={{
                minHeight: "100vh",
                backgroundImage: `
                    linear-gradient(
                        rgba(5,5,5,0.92),
                        rgba(5,5,5,0.95)
                    ),
                    url(${BgImage})
                `,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                py: { xs: 5, md: 10 },
                px: 2,
            }}
        >
            <Container maxWidth="lg">
                {/* Header */}
                <Box textAlign="center" mb={6}>
                    <Typography
                        variant="h2"
                        sx={{
                            fontWeight: 900,
                            fontSize: {
                                xs: "2.2rem",
                                sm: "3rem",
                                md: "4rem",
                            },
                            mb: 2,
                            letterSpacing: 1,
                            background:
                                "linear-gradient(90deg, #ffd786 0%, #b88942 100%)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            textShadow: "0px 0px 25px rgba(255,215,134,0.25)",
                        }}
                    >
                        Refund Policy
                    </Typography>

                    <Typography
                        sx={{
                            color: "#bdbdbd",
                            maxWidth: 720,
                            mx: "auto",
                            fontSize: {
                                xs: "0.95rem",
                                md: "1.1rem",
                            },
                            lineHeight: 1.8,
                        }}
                    >
                        Please read our refund and payment policies carefully
                        before making any donation or purchase on Vex
                        Conquer.
                    </Typography>
                </Box>

                {/* Cards */}
                <Grid container spacing={3}>
                    {sections.map((section, index) => (
                        <Grid item xs={12} md={6} key={index}>
                            <Paper
                                elevation={0}
                                sx={{
                                    height: "100%",
                                    background:
                                        "rgba(20,20,20,0.82)",
                                    border:
                                        "1px solid rgba(255,215,134,0.15)",
                                    borderRadius: "18px",
                                    p: { xs: 3, md: 4 },
                                    backdropFilter: "blur(6px)",
                                    transition: "0.3s ease",
                                    "&:hover": {
                                        transform: "translateY(-4px)",
                                        border:
                                            "1px solid rgba(255,215,134,0.35)",
                                        boxShadow:
                                            "0 0 25px rgba(255,215,134,0.08)",
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.5,
                                        mb: 2,
                                    }}
                                >
                                    {section.icon}

                                    <Typography
                                        variant="h5"
                                        sx={{
                                            color: "#ffd786",
                                            fontWeight: 700,
                                            fontSize: {
                                                xs: "1.1rem",
                                                md: "1.3rem",
                                            },
                                        }}
                                    >
                                        {section.title}
                                    </Typography>
                                </Box>

                                <Typography
                                    sx={{
                                        color: "#d6d6d6",
                                        lineHeight: 1.9,
                                        fontSize: {
                                            xs: "0.95rem",
                                            md: "1rem",
                                        },
                                    }}
                                >
                                    {section.content}
                                </Typography>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>

                {/* Important Notice */}
                <Paper
                    elevation={0}
                    sx={{
                        mt: 5,
                        background: "rgba(20,20,20,0.82)",
                        border:
                            "1px solid rgba(255,215,134,0.15)",
                        borderRadius: "18px",
                        p: { xs: 3, md: 4 },
                    }}
                >
                    <Typography
                        sx={{
                            color: "#ffb3b3",
                            fontWeight: 600,
                            mb: 1,
                            fontSize: {
                                xs: "1rem",
                                md: "1.1rem",
                            },
                        }}
                    >
                        Important Notice
                    </Typography>

                    <Typography
                        sx={{
                            color: "#d6d6d6",
                            lineHeight: 1.9,
                            fontSize: {
                                xs: "0.95rem",
                                md: "1rem",
                            },
                        }}
                    >
                        By donating or purchasing any digital item from
                        Vex Conquer, you acknowledge and agree to this
                        Refund Policy. All virtual items, currencies, and
                        services remain the property of Vex Conquer.
                    </Typography>
                </Paper>

                {/* Footer */}
                <Divider
                    sx={{
                        borderColor: "rgba(255,255,255,0.08)",
                        my: 6,
                    }}
                />

                <Box textAlign="center">
                    <Typography
                        sx={{
                            color: "#8b8b8b",
                            fontSize: {
                                xs: "0.8rem",
                                md: "0.9rem",
                            },
                            lineHeight: 1.8,
                        }}
                    >
                        Last Updated: May 2026
                        <br />
                        Vex Conquer © All Rights Reserved
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default RefundPolicy;